import { faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { createColumnHelper } from '@tanstack/react-table';
import { Checkbox, Input, InvertButton, PrimaryButton } from 'component-library';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ListViewFilterPanel from '../Filter/ListViewFilterPanel';
import { AppContext } from '../../contexts/AppContext';
import {
  buildSystemFilterOptionsByColumn,
  buildSystemFilterPanelColumns,
  FilterSystemsBySearch,
  GetSystemListData,
  getSystemFilterValueForColumn,
  systemDefaultColumnFilters,
  systemDefaultFilterSearch,
  systemListColumns,
  SystemColumnFilterKey,
  SystemsListProps,
} from '../../helpers/SystemListSetup';
import useListViewFilters from '../../helpers/UseListViewFilters';
import { System } from '../../models/ApiModels';
import { GetSystemsAsync } from '../../services/SystemService';
import { ActionType } from '../../store/actionTypes';
import { RootState } from '../../store/reducers/combine';
import useRequest from '../../utils/net/useRequest';
import PermissionGate, { ROLES } from '../../utils/PermissionGate';
import useWindowSize from '../../utils/UseWindowSize';
import Table from '../Table/Table';
import {
  BatchReassignOwnerPanel,
  useBatchReassignOwner,
} from './BatchReassignOwner';
import SystemsMiniDashboard from './SystemsMiniDashboard';

const systemsTableColumnHelper = createColumnHelper<SystemsListProps>();

function Systems() {
  const [searchParams, setSearchParams] = useSearchParams({ searchValue: '' });
  const size = useWindowSize();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentOemTenantId, isHumphreeUser, isBusinessCustomer, currentOem } = useContext(AppContext);
  const oems = useSelector((state: any) => state.oems.data);
  const packages = useSelector((state: any) => state.configurations.packages);
  const boatModels = useSelector((state: RootState) => state.boatModels.boatModels);
  const [systemFetchCounter, setSystemFetchCounter] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [toggleInternalSystems, setToggleInternalSystems] = useState(true);
  const {
    selectedByColumn: columnFilters,
    searchByColumn: filterOptionSearch,
    toggleValue,
    setOptionSearch,
    clearAll,
    activeCount: activeColumnFilterCount,
    applyFilters,
    buildFilterOptions,
  } = useListViewFilters<SystemColumnFilterKey>({
    initialSelectedByColumn: systemDefaultColumnFilters,
    initialSearchByColumn: systemDefaultFilterSearch,
  });
  const [systems = [], isSystemIsLoading, systemsGetError] = useRequest(
    () => GetSystemsAsync(),
    [currentOemTenantId, systemFetchCounter]
  );

  useEffect(() => {
    const searchValueFromParams = searchParams.get('searchValue');
    setSearchValue(searchValueFromParams || '');
  }, [searchParams]);

  useEffect(() => {
    dispatch({
      type: ActionType.GET_BOATMODELS,
      payload: boatModels,
    });
  }, [boatModels]);

  function goToSystemDetails(value: any, event: any) {
    if (event.ctrlKey || event.metaKey || event.which === 2) {
      window.open('systems/' + systems.find((system: System) => system.id === value.humphreeId).id, '_blank');
    } else navigate(value.humphreeId);
  }

  function goToCreate() {
    setTimeout(() => {
      navigate('/create');
    }, 1);
  }

  function resetPageToFirst() {
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  }

  const allSystems = systems || [];
  const filteredSystems = FilterSystemsBySearch(allSystems, searchValue, oems || [], packages || [], boatModels || []);
  const systemListData = GetSystemListData(filteredSystems, oems, packages, boatModels, toggleInternalSystems);
  const filteredSystemListData = useMemo(
    () => applyFilters(systemListData, getSystemFilterValueForColumn),
    [systemListData, applyFilters]
  );
  const filterOptionsByColumn = useMemo(
    () => buildSystemFilterOptionsByColumn(systemListData, buildFilterOptions),
    [systemListData, buildFilterOptions]
  );
  const isMobileFilterLayout = (size?.width || window.innerWidth) < 640;
  const filterPanelColumns = useMemo(() => buildSystemFilterPanelColumns(filterOptionsByColumn), [filterOptionsByColumn]);
  const hasIndirectCustomers = (oems || []).length > 0;
  const canShowBatchAction = isHumphreeUser || (isBusinessCustomer && hasIndirectCustomers);
  const batchReassignOwner = useBatchReassignOwner({
    oems: oems || [],
    canShowBatchAction,
    onReassignmentComplete: () => setSystemFetchCounter((prev) => prev + 1),
  });

  const tableColumns = useMemo(() => {
    if (!batchReassignOwner.isBatchMode) return systemListColumns;

    const batchSelectionColumn = systemsTableColumnHelper.display({
      id: 'batchSelect',
      header: 'Select',
      enableSorting: false,
      size: 88,
      cell: (info) => {
        const systemId = info.row.original.humphreeId || '';
        const isSelected = systemId.length > 0 && batchReassignOwner.selectedSystemIds.has(systemId);
        return (
          <div
            className={`flex items-center ${batchReassignOwner.canModifySelection ? 'cursor-pointer' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              batchReassignOwner.toggleSelectedSystem(systemId);
            }}
          >
            <Checkbox active={isSelected} onClick={() => undefined} />
          </div>
        );
      },
    });

    return [batchSelectionColumn, ...systemListColumns];
  }, [batchReassignOwner]);

  return (
    <div>
      <span className='prose-heading3'>Systems</span>
      {isSystemIsLoading ? (
        <div className='flex items-center flex-col gap-4 h-96 justify-center'>
          <span className='text-primary-400 prose-heading5'>Loading systems...</span>
          <div className='w-12 h-12 border-l-2 border-primary-400 rounded-full animate-spin'></div>
        </div>
      ) : (
        <>
          <div className='mt-14 border border-gray-10 p-4 flex flex-col md:flex-row md:justify-between md:items-center gap-3'>
            <div className='w-full md:w-1/2 xl:w-1/3 mb-1 md:mb-0'>
              <Input
                placeholder='Search for systems...'
                icon={faSearch}
                iconbefore
                value={searchValue}
                onChange={(value: string) => {
                  searchParams.set('searchValue', value);
                  setSearchValue(value);
                  resetPageToFirst();
                }}
              />
            </div>
            <div className='flex items-center gap-2 self-end md:self-auto'>
              <ListViewFilterPanel
                title='Filter systems'
                columns={filterPanelColumns}
                selectedByColumn={columnFilters}
                searchByColumn={filterOptionSearch}
                activeCount={activeColumnFilterCount}
                isMobile={isMobileFilterLayout}
                triggerClassName={`bg-gray-3 border border-gray-10 text-gray-80 prose-paragraphSmaller flex items-center ${window.innerWidth > 1500 ? 'h-8 px-3 gap-2' : 'h-6 px-2 gap-1'}`}
                onToggleValue={(key: SystemColumnFilterKey, value: string) => {
                  toggleValue(key, value);
                  resetPageToFirst();
                }}
                onSearchChange={(key: SystemColumnFilterKey, value: string) => setOptionSearch(key, value)}
                onClearAll={() => {
                  clearAll();
                  resetPageToFirst();
                }}
              />
              <PermissionGate roles={[ROLES.admin, ROLES.editor, ROLES.oemAdmin, ROLES.oemEditor]} rejected={undefined}>
                <InvertButton
                  size={size?.width > 1500 ? 'small' : 'tiny'}
                  label={batchReassignOwner.isBatchMode ? 'Exit batch mode' : 'Batch reassign owner'}
                  onClick={() => {
                    if (batchReassignOwner.isBatchMode) {
                      batchReassignOwner.exitBatchMode();
                    } else {
                      batchReassignOwner.enterBatchMode();
                    }
                  }}
                  disabled={batchReassignOwner.isReassigning}
                />
              </PermissionGate>
              <PermissionGate roles={[ROLES.admin]} rejected={undefined}>
                <PrimaryButton
                  onClick={() => goToCreate()}
                  icon={faPlus}
                  label='Create new system'
                  size={size?.width > 1500 ? 'small' : 'tiny'}
                />
              </PermissionGate>
            </div>
          </div>
          <BatchReassignOwnerPanel {...batchReassignOwner} />
          {!systemsGetError ? (
            <>
              <Table
                data={filteredSystemListData}
                columns={tableColumns}
                onClick={
                  batchReassignOwner.isBatchMode
                    ? batchReassignOwner.canModifySelection
                      ? (value: any) => batchReassignOwner.toggleSelectedSystem(value?.humphreeId || '')
                      : undefined
                    : (value: any, event?: any) => goToSystemDetails(value, event)
                }
                type='systems'
                setColumnVisibility={{
                  batchSelect: batchReassignOwner.isBatchMode,
                  humphreeId: false,
                  oemName: isHumphreeUser || currentOem?.parentOEMTenantId === null,
                  software: size?.width > 1600 || size?.width < 640,
                  boatModel: size?.width > 1200 || size?.width < 640,
                  hullNumber: size?.width > 1000 || size?.width < 640,
                }}
              />
              {isHumphreeUser && (
                <div className='flex justify-end items-center p-4 gap-2 border-x border-b border-gray-10'>
                  <Checkbox
                    active={toggleInternalSystems}
                    onClick={() => setToggleInternalSystems(!toggleInternalSystems)}
                  />
                  <p className='prose-labelStandard font-bold'>Hide Humphree Internal systems</p>
                </div>
              )}
              <SystemsMiniDashboard systems={filteredSystemListData} searchValue={searchValue} />
            </>
          ) : (
            <div className='flex justify-between items-center p-4 px-8 bg-error-100 border-x border-gray-10'>
              <span className='text-white prose-paragraphBase italic'>Could not load systems!</span>

              <span
                onClick={() => setSystemFetchCounter(systemFetchCounter + 1)}
                className='text-white prose-paragraphBase italic cursor-pointer underline'
              >
                Try again
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
export default Systems;
